<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';

import { useSession } from '@/auth/session';
import { readOnlyReason } from '@/utils/project-write';
import PageState from '@/components/PageState.vue';
import ProjectHealthDetails from '@/components/ProjectHealthDetails.vue';
import StatusChip from '@/components/StatusChip.vue';
import { usePortfolio } from '@/composables/usePortfolio';
import { eqsApi } from '@/api/eqs';
import type { Project, ProjectDetail } from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { healthFor, healthLabel, summaryFor } from '@/utils/portfolio';
import { buildGates, gateHeadline } from '@/utils/gates';

const route = useRoute();
const { data, loading, error, load } = usePortfolio();
const projectId = computed(() => String(route.params.projectId));
const detail = ref<ProjectDetail | null>(null);
const detailLoading = ref(true);
const detailError = ref('');
const healthDetailsOpen = ref(false);
const project = computed(() => detail.value);

/*
  只读横幅。所有人都能看所有项目（Tony 2026-08-18 定），
  不在项目组里的人按钮会是灰的——不说一句，人只会以为系统坏了。
*/
const { canWrite: accountCanWrite } = useSession();
const readOnlyNote = computed(() =>
  readOnlyReason(project.value as Project | null, accountCanWrite.value));
const summary = computed(() => {
  if (detail.value?.plan) return { ...detail.value.plan, projectId: detail.value.id };
  return data.value && project.value ? summaryFor(data.value, project.value.id) : undefined;
});
const health = computed(() => data.value && project.value ? healthFor(data.value, project.value.id) : undefined);
/*
  Gate 点按事实上色（Tony 8/19 问「这个节点是怎么变化的」）。
  判据全在 utils/gates.ts，那里有回测过的口径和注释。
*/
const gates = computed(() => buildGates(detail.value?.milestones ?? []));
const gateNote = computed(() => gateHeadline(gates.value));

/** GATE 条展开/收起，记在本机（存不进去也不该让页面打不开） */
const GATES_KEY = 'eqs.workspace.gates';
const gatesOpen = ref(readGatesOpen());
function readGatesOpen(): boolean {
  // 审查要求：未写过偏好时默认收起摘要，只有明确存过 'on' 才展开
  try {
    return window.localStorage.getItem(GATES_KEY) === 'on';
  } catch {
    return false;
  }
}
function toggleGates(): void {
  gatesOpen.value = !gatesOpen.value;
  try {
    window.localStorage.setItem(GATES_KEY, gatesOpen.value ? 'on' : 'off');
  } catch {
    // 隐私模式写不进去就算了，本次会话内仍然生效
  }
}


async function loadDetail() {
  detailLoading.value = true;
  detailError.value = '';
  try {
    detail.value = await eqsApi.projectDetail(projectId.value);
  } catch (caught) {
    detail.value = null;
    detailError.value = errorMessage(caught);
  } finally {
    detailLoading.value = false;
  }
}

// 换了项目先清掉旧详情：刷新时不再切成「加载中」，不清的话会短暂显示上一个项目
watch(projectId, () => {
  detail.value = null;
  void loadDetail();
}, { immediate: true });

/*
  健康度明细里就地改了东西（自评、挽回计划、项目类型、铺了计划）：健康度和项目详情都要重拉——
  只拉健康度的话，概览上的「项目类型」还显示「未维护」（0930）。
*/
function refreshAfterHealthChange(): void {
  void load();
  void loadDetail();
}

/* 审查 P1-02：17 入口按任务分组，避免等权页签占满手机首屏 */
const tabGroups = [
  { id: 'plan', label: '计划', tabs: [
    { suffix: 'overview', label: '概览' },
    { suffix: 'tasks', label: '任务看板' },
    { suffix: 'tracker', label: '任务跟踪' },
    { suffix: 'plan', label: '计划甘特' },
    { suffix: 'activities', label: '阶段工作' },
  ]},
  { id: 'eng', label: '工程资料', tabs: [
    { suffix: 'deliverables', label: '交付物' },
    { suffix: 'drawings', label: '图纸气泡' },
    { suffix: 'bom', label: 'BOM' },
    { suffix: 'moulds', label: '模具' },
    { suffix: 'files', label: '文件' },
  ]},
  { id: 'qa', label: '质量', tabs: [
    { suffix: 'dfmea', label: 'DFMEA' },
    { suffix: 'pfmea', label: 'PFMEA' },
    { suffix: 'control-plans', label: '控制计划' },
    { suffix: 'ppap', label: 'PPAP' },
  ]},
  { id: 'collab', label: '协作', tabs: [
    { suffix: 'issues', label: '问题 / 8D' },
    { suffix: 'changes', label: '变更' },
    { suffix: 'team', label: '团队' },
  ]},
];
// 当前子页所在的组默认展开；手机上其余组收起，点组名再展开
const currentTabSuffix = computed(() => {
  const parts = route.path.split('/');
  return parts[parts.length - 1] || 'overview';
});
function groupHasCurrent(group: { tabs: { suffix: string }[] }): boolean {
  return group.tabs.some((t) => t.suffix === currentTabSuffix.value);
}
</script>

<template>
  <!-- 已经有数据时刷新不切成「加载中」：切了会把打开着的健康度明细拆掉重建（0930，见 refresh-must-not-remount-panel） -->
  <PageState v-if="(loading && !data) || (detailLoading && !detail)" state="loading" title="正在打开项目工作区" message="读取项目组合和项目详情。" />
  <PageState v-else-if="error" state="error" title="项目身份数据不可用" message="项目工作区不会使用静态项目替代。" :detail="error" @retry="load" />
  <PageState v-else-if="detailError" state="error" title="项目详情不可用" message="项目工作区不会猜测详情数据。" :detail="detailError" @retry="loadDetail" />
  <PageState v-else-if="!project" state="error" title="项目不存在" :message="`组合接口未返回内部项目 ID ${projectId}`" />
  <template v-else-if="data && project">
    <!--
      项目头压成一行（1031 Tony：「先改项目页和 BOM 首屏」）。
      1366×768 笔记本上，原来的返回链接 + 三行身份 + 健康度块 + GATE 说明 + 两行页签吃掉约 410px，
      BOM 第一行数据落在 815px，首屏一行都看不到。现在身份、健康度、GATE、攻坚会并成一条，
      完整项目名在悬停里；窄了就自己折行，不横向溢出。
    -->
    <header class="workspace-bar">
      <div class="workspace-identity">
        <RouterLink class="workspace-back" to="/portfolio" aria-label="返回项目组合">← 项目组合</RouterLink>
        <span class="workspace-no">{{ project.projectNo }}</span>
        <h1 :title="project.projectName">{{ project.projectName }}</h1>
        <p>{{ project.customer || '客户未维护' }} · {{ project.projectManager || '项目经理未返回' }}</p>
      </div>
      <div class="workspace-tools">
        <button
          type="button"
          class="workspace-health-chip"
          :aria-label="`查看${project.projectName}健康度明细`"
          @click="healthDetailsOpen = true"
        >
          <StatusChip :value="health?.status || 'neutral'" :label="healthLabel(health?.status)" />
          <small>{{ (health?.reasons.length || 0) + (health?.dataIssues.length || 0) }} 项依据</small>
        </button>
        <!--
          GATE 条可收起（0821 布局优化），收起状态记在本机：干活的人（翻文件、改 BOM）不需要每页都看一遍 GATE。
          一句话结论常驻，展开才是六格路线。
        -->
        <span class="workspace-gate-note">{{ gateNote }}</span>
        <button type="button" class="inline-button" :aria-expanded="gatesOpen" @click="toggleGates">
          {{ gatesOpen ? '收起 GATE' : '展开 GATE' }}
        </button>
        <!-- 攻坚会（2026-08-25）：议程自动带上本项目全部未关闭问题（逾期在前）和黄灯节点 -->
        <RouterLink class="inline-button" :to="`/meetings?create&projectId=${projectId}`">发起攻坚会</RouterLink>
      </div>
    </header>
    <p v-if="readOnlyNote" class="workspace-readonly">👁 只读 —— {{ readOnlyNote }}</p>
    <ProjectHealthDetails
      v-if="healthDetailsOpen"
      :project="project"
      :health="health"
      :summary="summary"
      :milestones="data.milestones"
      :projects="data.projects"
      :as-of="data.asOf"
      @close="healthDetailsOpen = false"
      @declared="refreshAfterHealthChange"
    />
    <nav v-if="gatesOpen" class="gate-route" aria-label="项目 GATE1 至 GATE6 路线">
      <RouterLink
        v-for="gate in gates"
        :key="gate.code"
        :to="{ path: `/projects/${projectId}/plan`, query: { gate: gate.code } }"
        :class="[`tone-${gate.tone}`, { completed: gate.completed, current: gate.current }]"
        :aria-current="gate.current ? 'step' : undefined"
      >
        <i aria-hidden="true"></i>
        <span><b>{{ gate.code }}</b><small>{{ gate.title }}</small></span>
        <time>{{ gate.planDate ? formatDate(gate.planDate) : gate.status }}</time>
        <em v-if="gate.tone === 'overdue' || gate.driftDays">{{ gate.status }}</em>
      </RouterLink>
    </nav>
    <!--
      桌面：17 个入口排成一行，四组之间留一道缝，组名放在悬停里。1366 宽实测放得下（约 1050px），
      不收「更多」——藏起来的入口等于没有。再窄就整组折到下一行，不横向滚。
      手机（≤900px）仍用下面的分组折叠。两份导航按宽度只显示一份。
    -->
    <nav class="workspace-tabs workspace-tabs-flat" aria-label="项目工作区导航">
      <div
        v-for="group in tabGroups"
        :key="group.id"
        class="workspace-tab-run"
        role="group"
        :aria-label="group.label"
      >
        <RouterLink
          v-for="tab in group.tabs"
          :key="tab.suffix"
          :to="`/projects/${projectId}/${tab.suffix}`"
          :title="`${group.label} · ${tab.label}`"
        >{{ tab.label }}</RouterLink>
      </div>
    </nav>
    <nav class="workspace-tabs workspace-tabs-grouped" aria-label="项目工作区导航">
      <details
        v-for="group in tabGroups"
        :key="group.id"
        class="workspace-tab-group"
        :open="groupHasCurrent(group)"
      >
        <summary>{{ group.label }}</summary>
        <div class="workspace-tab-group-links">
          <RouterLink
            v-for="tab in group.tabs"
            :key="tab.suffix"
            :to="`/projects/${projectId}/${tab.suffix}`"
          >{{ tab.label }}</RouterLink>
        </div>
      </details>
    </nav>
    <RouterView v-slot="{ Component }">
      <component :is="Component" :project="project" :summary="summary" :portfolio="data" :detail="detail" />
    </RouterView>
  </template>
</template>

<style scoped>
/* 只读横幅：按钮变灰之前先说清楚为什么 */
.workspace-readonly {
  margin: 0 0 12px;
  padding: 9px 13px;
  border-left: 3px solid var(--blue);
  background: #EEF4FA;
  color: var(--ink);
  font-size: 12px;
  line-height: 1.8;
}
.gate-route {
  position: relative;
  display: grid;
  grid-template-columns: repeat(6, minmax(116px, 1fr));
  margin: 0 0 12px;
  border: 1px solid var(--line);
  background: var(--white);
}

/*
  一行项目头（1031）。上一版（1027 micro3）是三行身份 + 右侧健康度块 + 下面一行 GATE 说明，
  1366×768 下光项目头就到 260px。现在一条约 40px：返回 · 编号 · 名称 · 客户/项目经理 | 健康度 · GATE · 攻坚会。
  名称太长用省略号，全名在 title 里；放不下时右边那组整组折到第二行。
  类名换成新的，不再吃 styles.css 里给 .workspace-heading / .workspace-health / .back-link 写的大号样式。
*/
.workspace-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 20px;
  margin: 0 0 10px;
}
.workspace-identity { display: flex; flex: 1 1 360px; align-items: baseline; gap: 10px; min-width: 0; }
.workspace-back { flex: none; color: var(--muted); font-size: 13px; font-weight: 700; white-space: nowrap; }
.workspace-back:hover { color: var(--blue); }
.workspace-no { flex: none; color: var(--blue); font: 800 13px var(--display); letter-spacing: .06em; white-space: nowrap; }
.workspace-identity h1 {
  flex: 0 1 auto;
  min-width: 4em;
  margin: 0;
  overflow: hidden;
  font: 700 22px/1.3 var(--display);
  letter-spacing: -.01em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 客户名可能很长（「格拉默车辆内饰（上海）有限公司」）：先让它让位，项目名后让 */
.workspace-identity p {
  flex: 0 3 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.workspace-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px; }
.workspace-health-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 2px 10px 2px 4px;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--ink);
  background: var(--white);
  cursor: pointer;
}
.workspace-health-chip:hover { border-color: var(--blue); }
.workspace-health-chip small { color: var(--blue); font-size: 12px; font-weight: 700; white-space: nowrap; }
.workspace-gate-note {
  max-width: 24em;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 900px) {
  /* 手机上名称可以折行，别截成半个词 */
  .workspace-identity { flex-wrap: wrap; flex-basis: 100%; }
  .workspace-identity h1 { flex-basis: 100%; overflow-wrap: anywhere; white-space: normal; }
  .workspace-gate-note { max-width: 100%; white-space: normal; }
}

/*
 * 圆点单独占一行，文字在它下面。
 *
 * 原来是「圆点在左、文字在右」，连接线是一条横贯整条的绝对定位线——
 * 那条线正好从标题文字中间穿过去（GATE1 到 GATE6 每一格都被划了一道）。
 * 改成上下两层后，线只走圆点那一行，永远碰不到文字。
 */
.gate-route a {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-rows: 12px auto auto;
  gap: 9px 0;
  min-width: 0;
  padding: 12px 12px 11px;
  color: var(--muted);
  text-decoration: none;
}

/* 连接线：从本节点圆点连到下一个圆点，两端各留 6px 呼吸；最后一个不画 */
.gate-route a::after {
  content: "";
  position: absolute;
  top: 17px;
  left: 30px;
  right: -6px;
  height: 1px;
  background: var(--line);
}
.gate-route a:last-child::after { content: none; }

.gate-route a:hover { background: rgba(18, 109, 166, .04); }
.gate-route i {
  width: 12px;
  height: 12px;
  border: 2px solid var(--white);
  border-radius: 50%;
  background: var(--line-strong);
  box-shadow: 0 0 0 1px var(--line);
}
.gate-route span { display: flex; min-width: 0; gap: 6px; align-items: baseline; }
.gate-route b { color: var(--ink); font: 800 10px var(--display); white-space: nowrap; }
.gate-route small { overflow: hidden; font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.gate-route time { font-size: 12px; white-space: nowrap; }
.gate-route a.completed i { background: var(--green); box-shadow: 0 0 0 1px var(--green); }
.gate-route a.completed b { color: var(--green); }
/* 已过的段落连成绿色：一眼看出走到哪一关了 */
.gate-route a.completed::after { background: var(--green); }
/* 当前 Gate 用蓝底描边强调，不再反色成深块——整页只有它一处深色最扎眼 */
.gate-route a.current { background: rgba(0, 117, 174, .07); box-shadow: inset 0 0 0 1px var(--blue); color: var(--ink); }
.gate-route a.current b { color: var(--blue); }
.gate-route a.current i { background: var(--blue); box-shadow: 0 0 0 2px rgba(0, 117, 174, .2); }
/*
  逾期未通过：黄点。全库 26 个 GATE 属于这一类，原来和「还没轮到」长得一模一样。
  黄色压过「当前」的蓝——日子过了还没过关，比「正在做」更该被看见。
*/
.gate-route a.tone-overdue i {
  background: var(--warning-graphic);
  box-shadow: 0 0 0 2px rgba(224, 168, 0, .25);
}
.gate-route a.tone-overdue b { color: #7a4a10; }
.gate-route a.tone-overdue { background: #fdf6ec; box-shadow: inset 0 0 0 1px var(--warning-graphic); }
.gate-route em {
  display: block; margin-top: 2px;
  font-style: normal; font-size: 11px; white-space: nowrap;
}
.gate-route a.tone-overdue em { color: #7a4a10; font-weight: 700; }
.gate-route a.tone-passed em { color: var(--muted); }

@media (max-width: 900px) {
  /* 横向滚动时最后一格右边会露出线头，收掉 */
  .gate-route { display: flex; overflow-x: auto; }
  .gate-route a { flex: 0 0 142px; }
  .gate-route a::after { right: 0; }
}

/*
  桌面一行页签（1031）：链接的分隔线、字号、选中态沿用 styles.css 的 .workspace-tabs a，
  这里只把高度从 46 压到 38、去掉 112px 的最小宽度，四组之间用深一档的竖线隔开。
  styles.css 的 .workspace-tabs a:first-child 正好去掉每组第一个链接的左线，组间线由每组自己的左线补。
  窄屏整组折行时：行首那条左线靠 -1px 藏进 nav 边框；box-shadow 补行间线，最后一行的被 nav 的 overflow: hidden 裁掉。
*/
.workspace-tabs { margin: 0 0 14px; }
.workspace-tab-run {
  display: flex;
  flex: 1 1 auto;
  margin-left: -1px;
  border-left: 1px solid var(--line-strong);
  box-shadow: 0 1px 0 var(--line);
}
.workspace-tabs-flat a { flex: 1 1 auto; min-width: 0; height: 38px; padding: 0 10px; }
@media (min-width: 901px) {
  .workspace-tabs-grouped { display: none; }
}
@media (max-width: 900px) {
  .workspace-tabs-flat { display: none; }
}

/*
  手机（审查 P1-02）：17 个页签按任务分四组（计划 / 工程资料 / 质量 / 协作），每组一个 <details>，
  只展开当前子页所在的组，其余组点组名再展开——首屏不再被 6 行等权页签占满。
  桌面不用这一份（上面那条一行的）。
*/
.workspace-tab-group > summary {
  padding: 0 14px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
  line-height: 42px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
/* 当前子页所在的组，组名跟着变蓝（不支持 :has 的浏览器只是不变色） */
.workspace-tab-group:has(.router-link-active) > summary { color: var(--blue); }
.workspace-tab-group-links { display: flex; }
.workspace-tab-group-links a { flex: 1 0 auto; white-space: nowrap; }
.workspace-tab-group-links a.router-link-active { color: white; background: var(--blue); }

@media (max-width: 900px) {
  .workspace-tab-group { flex: 1 1 100%; min-width: 0; }
  .workspace-tab-group + .workspace-tab-group { border-top: 1px solid var(--line); }
  /* 组内链接一行横滑，不折行 */
  .workspace-tab-group-links { overflow-x: auto; border-top: 1px solid var(--line); }
  .workspace-tab-group-links a { flex: 0 0 auto; min-width: 96px; height: 42px; padding: 0 12px; }
}
</style>
